import { useCallback } from 'react';
import { MessageStatus } from '@connect-x-app/shared-domain-models';

import {
  useAuthenticationStore,
  useChannelSessionStore,
  useChannelStore,
  useFlightRoomSessionStore,
  useFlightRoomStore,
  useMessageStore,
} from '../stores';
import { useChatMessageSocketSendEvents } from './socketEvents';

export const useChatMessageNotifications = () => {
  const { user } = useAuthenticationStore();
  const { selectedFlightRoomId } = useFlightRoomSessionStore();
  const { flightRooms, updatePartialFlightRoom } = useFlightRoomStore();
  const { selectedChannelId } = useChannelSessionStore();
  const { channelsMap, updatePartialCustomChannel, updatePartialMainChannel } = useChannelStore();
  const { updateMessageStatus } = useMessageStore();
  const { sendChatReadMessage } = useChatMessageSocketSendEvents();

  const incrementFlightRoomMessageCount = useCallback(
    (chatroomId: string) => {
      if (chatroomId === selectedFlightRoomId) {
        return;
      }

      const room = flightRooms.get(chatroomId);
      if (room) {
        updatePartialFlightRoom(room.id, {
          unreadMessagesCount: (room?.unreadMessagesCount ?? 0) + 1,
        });
      }
    },
    [flightRooms, selectedFlightRoomId, updatePartialFlightRoom],
  );

  const incrementChannelMessageCount = useCallback(
    (chatroomId: string, channelId: string, messageId?: string) => {
      if (channelId === selectedChannelId) {
        if (messageId && user?.externalId) {
          sendChatReadMessage({ channelId, chatroomId, messageId });
          updateMessageStatus({
            channelId,
            messageId,
            status: MessageStatus.READ,
            userExternalId: user.externalId,
          });
        }
        return;
      }

      const channels = channelsMap.get(chatroomId);
      if (channels) {
        if (channels.mainChannel.id === channelId) {
          updatePartialMainChannel(chatroomId, {
            firstUnreadMessage: channels.mainChannel.firstUnreadMessage ?? messageId,
            unreadMessagesCount: (channels.mainChannel.unreadMessagesCount ?? 0) + 1,
          });
        } else {
          const index = channels.customChannels.findIndex(c => c.id === channelId);
          if (index !== -1) {
            updatePartialCustomChannel(chatroomId, channelId, {
              firstUnreadMessage: channels.customChannels[index].firstUnreadMessage ?? messageId,
              unreadMessagesCount: (channels.customChannels[index].unreadMessagesCount ?? 0) + 1,
            });
          }
        }
      }
    },
    [
      channelsMap,
      selectedChannelId,
      sendChatReadMessage,
      updateMessageStatus,
      updatePartialCustomChannel,
      updatePartialMainChannel,
      user?.externalId,
    ],
  );

  return { incrementChannelMessageCount, incrementFlightRoomMessageCount };
};
